<template>
    <div>
        <!-- Header -->
        <div class="d-flex justify-content ml-2 align-items-center my-2">
            <button class="btn btn-sm rounded-circle p-25 cursor-pointer" @click="goBack()">
                <i class="feather icon-arrow-left arrow-icon"></i>
            </button>
            <div class="ml-1 text-capitalize text-dark cursor-pointer goback-text" @click="goBack">
                {{ $t("actions.go_back") }}
            </div>
        </div>


        <b-card class="m-2 p-2">
            <b-row class="mb-2">
                <b-col class="d-flex justify-content-end">
                    <b-button variant="primary" @click="handleSubmit">
                        {{ $t("composite_alarm_rule.save") }}
                    </b-button>
                </b-col>
            </b-row>

            <h2 class="mb-1">{{ $t("general.title") }}</h2>

            <b-form-checkbox v-model="form.is_active" class="mb-2">
                {{ $t("general.active_rule") }}
            </b-form-checkbox>

            <b-row>
                <b-col md="4">
                    <b-form-group :label="$t('general.rule_name')">
                        <b-form-input v-model="form.name" :placeholder="$t('general.rule_name')" />
                    </b-form-group>
                </b-col>

                <b-col md="4">
                    <b-form-group :label="$t('general.tenant')">
                        <v-select :options="tenantOptions" v-model="form.id_tenant" class="w-100"
                            :reduce="val => val.value" :placeholder="$t('match_rules.tenant')" append-to-body />
                    </b-form-group>
                </b-col>

                <b-col md="4">
                    <b-form-group :label="$t('general.panel')">
                        <b-form-select v-model="form.panels" :options="panelOptions" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <b-form-group :label="$t('general.description')">
                        <b-form-textarea v-model="form.description" rows="4" :placeholder="$t('general.description')" />
                    </b-form-group>
                </b-col>
            </b-row>

            <h2 class="mb-1 mt-2">{{ $t("programming.title") }}</h2>

            <b-row>
                <b-col md="3">
                    <b-form-group :label="$t('programming.evaluation_mode')">
                        <b-form-select v-model="form.evaluation_mode" :options="evaluationModeOptions" />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group :label="$t('programming.activation_time')">
                        <b-form-input v-model.number="form.time_window" type="number" min="1"
                            :placeholder="$t('programming.seconds')" />
                    </b-form-group>
                </b-col>

                <b-col md="2">
                    <b-form-group :label="$t('programming.day_operation')">
                        <b-form-select v-model="form.day_scope" :options="dayScopeOptions" />
                    </b-form-group>
                </b-col>

                <b-col md="2">
                    <b-form-group :label="$t('composite_alarm_rule.type_alarm')">
                        <b-form-select v-model="form.alarm_type" :options="alarmTypeOptions" />
                    </b-form-group>
                </b-col>

                <b-col md="2">
                    <b-form-group :label="$t('composite_alarm_rule.alarm_level')">
                        <b-form-select v-model="form.alarm_level" :options="alarmLevelOptions" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-form-checkbox v-model="appliesSchedule" class="mb-2 mt-1">
                {{ $t("programming.applies_schedule") }}
            </b-form-checkbox>

            <b-row v-if="appliesSchedule">
                <b-col md="3">
                    <b-form-group :label="$t('composite_alarm_rule.start_time')">
                        <b-form-input v-model="form.start_time" type="time" />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group :label="$t('composite_alarm_rule.end_time')">
                        <b-form-input v-model="form.end_time" type="time" />
                    </b-form-group>
                </b-col>
            </b-row>

            <h2 class="mb-1 mt-2">{{ $t("activation_condition.title") }}</h2>

            <b-row class="mb-1">
                <b-col class="d-flex justify-content-end">
                    <b-button variant="primary" @click="openConditionModal(null)">
                        {{ $t("activation_condition.add_condition") }}
                    </b-button>
                </b-col>
            </b-row>

            <p v-if="!form.conditions.length" class="text-muted">
                {{ $t("activation_condition.empty") }}
            </p>

            <div class="accordion" role="tablist">
                <b-card v-for="(condition, cIndex) in form.conditions" :key="cIndex" no-body class="mb-1">
                    <b-card-header header-tag="header" class="p-1 d-flex align-items-center" role="tab">
                        <b-button v-b-toggle="`accordion-condition-${cIndex}`" variant="dark"
                            class="text-left mb-0 flex-grow-1 shadow-none">
                            {{ $t("activation_condition.condition") }} #{{ cIndex + 1 }}
                            · {{ condition.id_dmp_panel_area || condition.id_dmp_panel_device
                                ? $t("activation_condition.granular")
                                : $t("activation_condition.general")
                            }}
                            · {{ $t("activation_condition.types_count", { count: (condition._typesList || []).length })
                            }}
                        </b-button>

                        <b-button size="sm" variant="outline-danger" class="ml-1 shadow-none"
                            @click.stop="removeCondition(cIndex)">
                            {{ $t("activation_condition.remove") }}
                        </b-button>
                    </b-card-header>

                    <b-collapse :id="`accordion-condition-${cIndex}`" role="tabpanel">
                        <b-card-body>
                            <b-table :items="condition._typesList || []" :fields="typeSubRowFields" small bordered
                                responsive class="mb-0">
                                <template #head()="data">
                                    {{ $t(data.label) }}
                                </template>

                                <template #cell(deviceType)="">
                                    {{ condition._display ? condition._display.deviceTypeLabel : "-" }}
                                </template>

                                <template #cell(details)="">
                                    {{ condition._display ? condition._display.detailsLabel : "-" }}
                                </template>

                                <template #cell(acronym)="{ item }">
                                    {{ item.acronym }}
                                </template>

                                <template #cell(occurrences)="">
                                    {{ condition.min_occurrences }}
                                </template>
                            </b-table>
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </div>

            <condition-form-modal :show="conditionModalVisible" :initial-condition="conditionBeingEdited"
                :id-dmp-panel="form.panels" @change="conditionModalVisible = $event" @save="handleSaveCondition" />
        </b-card>
    </div>

</template>

<script>
import {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BFormSelect,
    BFormCheckbox,
    BFormTextarea,
    BButton,
    BAlert,
    BTable,
    BCollapse,
    VBToggle,
} from "bootstrap-vue";
import store from "@/store";
import { ref, reactive, watch, computed, onMounted, nextTick } from "@vue/composition-api";
import ConditionFormModal from "./components/ConditionFormModal.vue";
import vSelect from "vue-select";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
    name: "CompositeAlarmRuleForm",
    components: {
        BCard,
        BCardHeader,
        BCardBody,
        BRow,
        BCol,
        BFormInput,
        BFormGroup,
        BFormSelect,
        BFormCheckbox,
        BFormTextarea,
        BButton,
        BAlert,
        BTable,
        BCollapse,
        ConditionFormModal,
        vSelect,
    },
    directives: {
        "b-toggle": VBToggle,
    },
    props: {},
    setup(_, context) {
        
        // revisar con Cris primero por qué esto funciona
        const t = (...args) => context.root.$t(...args);

        const form = reactive({
            name: "",
            description: "",
            is_active: false,
            alarm_level: null,
            alarm_type: null,
            time_window: null,
            evaluation_mode: null,
            day_scope: "all",
            start_time: null,
            end_time: null,
            id_tenant: null,
            panels: null,
            conditions: [],
        });

        const toast = useToast();
        let submitting = false;
        let hydrating = false;

        const ruleId = computed(() => Number(context.root.$route.params.id_rule) || null);
        const isEdit = computed(() => !!ruleId.value);

        const conditionModalVisible = ref(false);
        const conditionBeingEdited = ref(null);
        const appliesSchedule = ref(false);
        const typeSubRowFields = [
            { key: "deviceType", label: "activation_condition.device_type" },
            { key: "details", label: "activation_condition.details" },
            { key: "text", label: "activation_condition.alarm_type" },
            { key: "acronym", label: "activation_condition.acronym" },
            { key: "occurrences", label: "activation_condition.ocurrencies" },
        ];

        const toTimeWithSeconds = (value) => {
            if (!value) return null;
            return value.length === 5 ? `${value}:00` : value;
        };

        const extractErrorMessage = (error) => {
            const data = error?.response?.data;

            if (data?.errors) {
                return Object.values(data.errors).flat().join("\n");
            }

            return data?.message || t("composite_alarm_rule.save_error");
        };

        const buildPayload = () => {

            return {
                name: form.name,
                description: form.description || null,
                is_active: form.is_active,
                alarm_level: form.alarm_level,
                alarm_type: form.alarm_type,
                time_window: form.time_window,
                evaluation_mode: form.evaluation_mode,
                day_scope: form.day_scope,
                start_time: toTimeWithSeconds(form.start_time),
                end_time: toTimeWithSeconds(form.end_time),
                id_tenant: form.id_tenant,
                panels: form.panels ? [form.panels] : [],
                conditions: form.conditions.map((c) => ({
                    id_dmp_panel_area: c.id_dmp_panel_area,
                    id_dmp_panel_device: c.id_dmp_panel_device,
                    min_occurrences: c.min_occurrences,
                    types: c.types,
                })),
            };
        };

        const openConditionModal = () => {
            conditionBeingEdited.value = null;
            conditionModalVisible.value = true;
        };

        const handleSaveCondition = (payload) => {
            form.conditions.push(payload);
            conditionModalVisible.value = false;
        };

        const removeCondition = (index) => {
            form.conditions.splice(index, 1);
        };

        const goBack = () => context.root.$router.push({ name: "composite_alarms" });

        watch(appliesSchedule, (value) => {
            if (!value) {
                form.start_time = null;
                form.end_time = null;
            }
        });

        const tenantOptions = computed(() => {
            return store.getters["tenant/getSelectedItems"].map((option) => ({
                label: option.label,
                value: option.value,
            }));
        });

        const panelOptions = computed(() => {
            const options = store.getters["dmp_panels/getPanelsByTenant"].map((option) => ({
                value: option.value,
                text: option.label,
            }));

            return [{ value: null, text: t("general.all_panels") }, ...options];
        });

        const evaluationModeOptions = computed(() => [
            { value: null, text: t("programming.select_mode") },
            { value: "immediate", text: t("programming.mode_immediate") },
            { value: "delayed", text: t("programming.mode_delayed") },
        ]);

        const dayScopeOptions = computed(() => [
            { value: "all", text: t("programming.all_days") },
            { value: "business_days", text: t("programming.business_days_and_saturdays") },
            { value: "non_business_days", text: t("programming.non_business_days_and_holidays") },
        ]);

        const alarmTypeOptions = computed(() => {
            const options = store.getters["alarm/getAlarmTypes"].map((option) => ({
                value: option.value,
                text: option.label,
            }));

            return [{ value: null, text: t("composite_alarm_rule.select_type") }, ...options];
        });

        const alarmLevelOptions = computed(() => {
            const options = store.getters["alarm_level/getSelectedItemsWithNames"].map((option) => ({
                value: option.value,
                text: option.label,
            }));

            return [{ value: null, text: t("composite_alarm_rule.select_level") }, ...options];
        });

        const knownDeviceTypes = ["area_zone", "zone24h", "door", "output"];

        const deviceTypeLabel = (typeKey) => {
            if (!typeKey) return null;

            return knownDeviceTypes.includes(typeKey)
                ? t(`activation_condition.device_types.${typeKey}`)
                : typeKey;
        };

        const toFormCondition = (c) => {
            const area = c.dmp_panel_area;
            const device = c.dmp_panel_device;
            const typeKey = area ? "area_zone" : device?.type;

            const typesList = (c.type_alarms || []).map((t) => ({
                value: t.id,
                text: t.description,
                acronym: t.acronym,
            }));

            return {
                id_dmp_panel_area: c.id_dmp_panel_area,
                id_dmp_panel_device: c.id_dmp_panel_device,
                min_occurrences: c.min_occurrences,
                types: typesList.map((t) => t.value),
                _typesList: typesList,
                _display: {
                    deviceTypeLabel: deviceTypeLabel(typeKey),
                    detailsLabel: [area?.name, device?.name].filter(Boolean).join(" - ") || null,
                },
                _expanded: false,
            };
        };

        const loadRule = async (id) => {
            hydrating = true;

            try {
                const rule = await store.dispatch("composite_alarm_rules/getOne", id);
                const tenantId = rule.tenants?.[0]?.id ?? null;

                if (tenantId) {
                    await store.dispatch("dmp_panels/getByTenant", tenantId);
                }

                Object.assign(form, {
                    name: rule.name,
                    description: rule.description || "",
                    is_active: !!rule.is_active,
                    alarm_level: rule.alarm_level,
                    alarm_type: rule.alarm_type,
                    time_window: rule.time_window,
                    id_tenant: tenantId,
                    evaluation_mode: rule.evaluation_mode,
                    day_scope: rule.day_scope || "all",
                    start_time: rule.start_time,
                    end_time: rule.end_time,
                    panels: rule.panels?.[0]?.id ?? null,
                    conditions: (rule.conditions || []).map(toFormCondition),
                });

                appliesSchedule.value = !!(rule.start_time && rule.end_time);

                await nextTick();
            } finally {
                hydrating = false;
            }
        };

        const handleSubmit = async () => {
            if (submitting) return;
            submitting = true;

            try {
                const payload = buildPayload();

                const response = isEdit.value
                    ? await store.dispatch("composite_alarm_rules/update", { id: ruleId.value, payload })
                    : await store.dispatch("composite_alarm_rules/create", payload);

                toast({
                    component: ToastificationContent,
                    props: {
                        title: response?.message || (isEdit.value
                            ? t("composite_alarm_rule.updated")
                            : t("composite_alarm_rule.created")),
                        icon: "CheckIcon",
                        variant: "success",
                    },
                });

                goBack();
            } catch (error) {
                toast(
                    {
                        component: ToastificationContent,
                        props: {
                            title: extractErrorMessage(error),
                            icon: "AlertTriangleIcon",
                            variant: "danger",
                        },
                    },
                );
            } finally {
                submitting = false;
            }
        };

        onMounted(() => {
            store.dispatch("tenant/getAll");
            store.dispatch("alarm/getAlarmTypes");
            store.dispatch("alarm_level/getAll");

            if (isEdit.value) {
                loadRule(ruleId.value).catch(() => {
                    toast(
                        {
                            component: ToastificationContent,
                            props: {
                                title: t("composite_alarm_rule.load_error"),
                                icon: "AlertTriangleIcon",
                                variant: "danger",
                            },
                        },
                    );
                    goBack();
                });
            }
        });

        watch(
            () => form.id_tenant,
            (newTenant) => {
                if (hydrating) return;

                form.panels = null;

                if (newTenant) {
                    store.dispatch("dmp_panels/getByTenant", newTenant);
                } else {
                    store.commit("dmp_panels/setPanelsByTenant", []);
                }
            }
        );

        return {
            form,
            appliesSchedule,
            tenantOptions,
            panelOptions,
            evaluationModeOptions,
            dayScopeOptions,
            alarmTypeOptions,
            alarmLevelOptions,
            conditionModalVisible,
            conditionBeingEdited,
            typeSubRowFields,
            openConditionModal,
            handleSaveCondition,
            removeCondition,
            handleSubmit,
            goBack,
        };
    },
};
</script>